/**
 * DocsSliderContent — the 420 px right-edge drawer surface (Wave 2.2).
 *
 * Composes the leaf primitives shipped in Wave 2:
 *
 *   ┌──────── header ────────────────────────────────┐
 *   │ <DocsSearch/>                            [ × ] │
 *   ├──────── body (split on `xl`) ──────────────────┤
 *   │  <DocsTree/>       │       <DocRenderer/>      │
 *   └────────────────────┴───────────────────────────┘
 *
 * Contract summary — from task 2.2 and requirements 1.1–1.4 · 6.1–6.3:
 *
 *  - Root is `<aside role="complementary" aria-label="Documentation">`.
 *  - Layout: split (`xl:flex-row`) at ≥ 1280 px viewports; stacked below.
 *  - On open (false → true), keyboard focus moves to the search input.
 *  - On close (true → false), focus returns to `triggerRef.current`
 *    when the caller provided one (Req 1.3).
 *  - Initial doc resolves via `initialDocPath` if defined and ending in
 *    `.md`; otherwise `routeToDefaultDoc(currentRoute)` (algorithm A3).
 *  - Local state (`{path, anchor}`) only updates from user interaction —
 *    tree selection, search pick, or renderer link click (design § Component 2).
 *  - `Esc` calls `onClose()` (Req 1.3).
 *  - `dangerouslySetInnerHTML` is NEVER used — Markdown flows through the
 *    `renderMarkdownSafely` pipeline inside `<DocRenderer/>`.
 *
 * This component owns no fetching, no persistence, and no route awareness
 * beyond the initial-doc calculation. The `DocsSliderProvider` (Wave 2.1)
 * feeds it fresh props on every route change.
 *
 * @see .kiro/specs/help-docs-panel/requirements.md#requirement-1-docs-slider-drawer
 * @see .kiro/specs/help-docs-panel/requirements.md#requirement-6-route-to-default-section-mapping
 * @see .kiro/specs/help-docs-panel/design.md — Component 2 (DocsSliderContent)
 *
 * @packageDocumentation
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { KeyboardEvent, RefObject } from "react";
import { DocRenderer } from "./DocRenderer";
import { DocsSearch } from "./DocsSearch";
import { DocsTree } from "./DocsTree";
import { routeToDefaultDoc } from "./routeMapping";
import type { DocsManifest } from "./types";

// ---------------------------------------------------------------------------
// Public props
// ---------------------------------------------------------------------------

/**
 * Props accepted by {@link DocsSliderContent}.
 */
export interface DocsSliderContentProps {
  /**
   * Whether the drawer is visible. When `false` the component renders
   * `null`, but its focus-return effect still runs so `triggerRef.current`
   * can be re-focused on close (Req 1.3).
   */
  open: boolean;

  /**
   * Invoked when the user closes the drawer via the header `×` button or
   * by pressing `Esc` inside the drawer.
   */
  onClose(): void;

  /**
   * Optional ref to the element that originally triggered the open
   * action (e.g. a help button in the app header). When the drawer
   * transitions from open → closed, keyboard focus is restored to
   * `triggerRef.current` (Req 1.3).
   */
  triggerRef?: RefObject<HTMLElement>;

  /**
   * Optional manifest path to open on mount, e.g. `"api/statements.md"`.
   * When defined AND ending in `.md`, it wins over the route-based
   * default; anchor may be supplied separately via {@link initialAnchor}.
   */
  initialDocPath?: string;

  /**
   * Optional heading slug to scroll into view on mount, e.g.
   * `"collection-rate"`. Passed through to `<DocRenderer/>`.
   */
  initialAnchor?: string;

  /**
   * Current app route, e.g. `"/statements/abc-123"`. When
   * {@link initialDocPath} is not supplied, the initial doc is derived
   * from `routeToDefaultDoc(currentRoute)` (Req 6.1, 6.2).
   */
  currentRoute: string;

  /**
   * The build-time docs manifest handed down from the provider. Consumed
   * by `<DocsTree/>` and `<DocsSearch/>`.
   */
  manifest: DocsManifest;
}

// ---------------------------------------------------------------------------
// Initial doc resolution
// ---------------------------------------------------------------------------

/**
 * Seed the local `{ path, anchor }` state on mount.
 *
 * Precedence:
 *  1. `initialDocPath` (when defined and ending in `.md`), paired with
 *     `initialAnchor` when defined.
 *  2. `routeToDefaultDoc(currentRoute)` — algorithm A3. When the route
 *     is malformed (empty or not starting with `/`) we fall back to `"/"`
 *     so we never let A3 throw.
 *
 * @internal
 */
function resolveInitial(
  initialDocPath: string | undefined,
  initialAnchor: string | undefined,
  currentRoute: string,
): { path: string; anchor?: string } {
  if (typeof initialDocPath === "string" && initialDocPath.endsWith(".md")) {
    return { path: initialDocPath, anchor: initialAnchor };
  }
  const safeRoute =
    typeof currentRoute === "string" && currentRoute.startsWith("/")
      ? currentRoute
      : "/";
  const fallback = routeToDefaultDoc(safeRoute);
  return {
    path: fallback.path,
    anchor: initialAnchor ?? fallback.anchor,
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * The 420 px right-edge documentation drawer.
 *
 * @see DocsSliderContentProps
 */
export function DocsSliderContent(
  props: DocsSliderContentProps,
): JSX.Element | null {
  const {
    open,
    onClose,
    triggerRef,
    initialDocPath,
    initialAnchor,
    currentRoute,
    manifest,
  } = props;

  const containerRef = useRef<HTMLElement>(null);
  const wasOpenRef = useRef<boolean>(false);

  // Local `{path, anchor}` state — seeded on mount per design § Component 2
  // ("Local state manages currentDocPath and currentAnchor; updates from tree
  // selection, search pick, or renderer's internal onLinkNavigate"). Also
  // refreshed on route change while the slider is sticky-open — see the
  // route-change effect below (Req 6.1–6.3).
  const [current, setCurrent] = useState<{ path: string; anchor?: string }>(
    () => resolveInitial(initialDocPath, initialAnchor, currentRoute),
  );

  // Whether `current` was seeded by `routeToDefaultDoc` (`true`) or by a
  // user-controlled source (`false`). "User-controlled" covers:
  //
  //  - a caller-supplied `initialDocPath` prop (deep-link or persisted
  //    state — the parent decided which doc to open),
  //  - a tree click,
  //  - a search pick,
  //  - a renderer link navigation.
  //
  // Once flipped to `false` it stays `false` for the rest of the mount so
  // the route-change effect below does not overwrite the user's choice
  // (Req 6.1 precedence — "route change refreshes default doc only when
  // no user interaction has occurred since the last route change").
  const [isDefaultDoc, setIsDefaultDoc] = useState<boolean>(
    () =>
      !(typeof initialDocPath === "string" && initialDocPath.endsWith(".md")),
  );

  // Records the route the effect last processed. Prevents redundant
  // re-renders on mount (where `current` was already seeded by
  // `resolveInitial`) and on `isDefaultDoc` transitions (which can only
  // flip `true → false` and therefore never demand re-application).
  const lastProcessedRouteRef = useRef<string | null>(null);

  // Focus management (Req 1.2, 1.3).
  //
  // Detects the open ↔ closed transitions using a ref-tracked "was open"
  // flag so we do NOT refocus on unrelated re-renders (e.g. a tree click
  // that updates `current`). The ref sits outside `deps` on purpose —
  // React 18 batching guarantees the effect sees a consistent `open`
  // value paired with the current DOM.
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      const input =
        containerRef.current?.querySelector<HTMLInputElement>(
          'input[role="combobox"]',
        ) ?? null;
      if (input !== null) {
        input.focus();
      }
    } else if (!open && wasOpenRef.current) {
      const trigger = triggerRef?.current ?? null;
      if (trigger !== null) {
        trigger.focus();
      }
    }
    wasOpenRef.current = open;
  }, [open, triggerRef]);

  // Route-change default refresh (Req 6.1, 6.2, 6.3 + Req 1 state machine:
  // "route change with sticky-open preference updates default doc, focuses
  // search").
  //
  // Fires whenever `currentRoute`, `open`, or `isDefaultDoc` changes.
  // Applies the new default only when ALL of the following hold:
  //
  //  1. `open === true`     — a closed drawer has no visible render pane, so
  //                           we let `current` stay stable and pick up the
  //                           new default the next time this effect fires
  //                           after the drawer reopens.
  //  2. `isDefaultDoc`      — the user has not picked a doc since the last
  //                           default was seeded; overwriting a user pick
  //                           would violate the precedence rule.
  //  3. The route actually changed since the last fire — mount and
  //     `isDefaultDoc` transitions both fire this effect, but neither
  //     represents a route change.
  //
  // When those hold, recompute `routeToDefaultDoc(currentRoute)`, apply
  // the new `{path, anchor}` (bailing out via `setCurrent`'s prev-check if
  // the value is unchanged), and refocus the search input.
  useEffect(() => {
    if (!open) return;
    if (!isDefaultDoc) return;
    if (lastProcessedRouteRef.current === currentRoute) return;
    lastProcessedRouteRef.current = currentRoute;

    const safeRoute =
      typeof currentRoute === "string" && currentRoute.startsWith("/")
        ? currentRoute
        : "/";
    const next = routeToDefaultDoc(safeRoute);
    setCurrent((prev) =>
      prev.path === next.path && prev.anchor === next.anchor
        ? prev
        : { path: next.path, anchor: next.anchor },
    );

    const input =
      containerRef.current?.querySelector<HTMLInputElement>(
        'input[role="combobox"]',
      ) ?? null;
    if (input !== null) {
      input.focus();
    }
  }, [currentRoute, open, isDefaultDoc]);

  const handleTreeSelect = useCallback(
    (path: string, anchor?: string): void => {
      setCurrent({ path, anchor });
      setIsDefaultDoc(false);
    },
    [],
  );

  const handleSearchPick = useCallback(
    (path: string, anchor?: string): void => {
      setCurrent({ path, anchor });
      setIsDefaultDoc(false);
    },
    [],
  );

  const handleLinkNavigate = useCallback((path: string): void => {
    setCurrent({ path, anchor: undefined });
    setIsDefaultDoc(false);
  }, []);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLElement>): void => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    },
    [onClose],
  );

  if (!open) return null;

  // Root element: a `<div>` carrying the explicit `role="complementary"` (as
  // the task specifies verbatim). Using `<aside>` would trip
  // `jsx-a11y/no-redundant-roles` because `<aside>` implies that same role,
  // and dropping the attribute weakens the contract with the DocsSliderProvider
  // that asserts on the explicit landmark.
  return (
    <div
      ref={containerRef as React.RefObject<HTMLDivElement>}
      role="complementary"
      aria-label="Documentation"
      onKeyDown={handleKeyDown}
      className="fixed top-16 right-0 z-[45] flex h-[calc(100vh-64px)] w-[420px] flex-col border-l border-gray-200 bg-white shadow-lg"
    >
      <header className="flex h-14 shrink-0 items-center gap-2 border-b border-gray-200 px-3">
        <div className="min-w-0 flex-1">
          <DocsSearch manifest={manifest} onPick={handleSearchPick} />
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close documentation"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <span aria-hidden="true" className="text-xl leading-none">
            ×
          </span>
        </button>
      </header>
      <div className="flex flex-1 flex-col overflow-hidden xl:flex-row">
        <nav
          aria-label="Documentation navigation"
          className="h-1/3 shrink-0 overflow-auto border-b border-gray-200 xl:h-full xl:w-1/2 xl:border-b-0 xl:border-r"
        >
          <DocsTree
            manifest={manifest}
            selectedPath={current.path}
            onSelect={handleTreeSelect}
          />
        </nav>
        <div className="flex-1 overflow-auto p-4 xl:w-1/2">
          <DocRenderer
            docPath={current.path}
            anchor={current.anchor}
            onLinkNavigate={handleLinkNavigate}
          />
        </div>
      </div>
    </div>
  );
}
